import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { fetchProfilesByIds } from '@/utils/fetchProfilesByIds';
import { addToTotal, hoursToMinutes, minutesToHours, validateDuration } from '@/utils/volunteerHours';
import { logger } from '@/utils/logger';

export interface HourRequest {
  id: string;
  volunteer_id: string;
  event_id: string;
  task_id: string | null;
  task_title: string | null;
  start_time: string;
  end_time: string;
  hours: number;
  description: string | null;
  created_at: string;
  profiles?: HourRequestProfile | null;
}

export interface HourRequestProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  profile_image: string | null;
}

export function useHourRequests(
  selectedEventId: string | null,
  userId: string | undefined,
  refetch: () => void,
  callbacks?: { onError?: (message: string) => void; onSuccess?: () => void },
) {
  const onError = callbacks?.onError;
  const onSuccess = callbacks?.onSuccess;
  const [hourRequests, setHourRequests] = useState<HourRequest[]>([]);
  const [hourRequestsCount, setHourRequestsCount] = useState(0);
  const [loadingHourRequests, setLoadingHourRequests] = useState(false);
  const [showHourRequestsModal, setShowHourRequestsModal] = useState(false);

  useEffect(() => {
    setHourRequests([]);
    setHourRequestsCount(0);
    setShowHourRequestsModal(false);
  }, [selectedEventId]);

  const fetchHourRequestsCount = useCallback(async () => {
    if (!selectedEventId) {
      setHourRequestsCount(0);
      return;
    }
    try {
      const { count, error: err } = await supabase
        .from('volunteer_activities')
        .select('*', { count: 'exact', head: true })
        .eq('event_id', selectedEventId)
        .is('task_id', null);
      if (err) {
        setHourRequestsCount(0);
        return;
      }
      setHourRequestsCount(count || 0);
    } catch {
      setHourRequestsCount(0);
    }
  }, [selectedEventId]);

  const fetchHourRequests = useCallback(async () => {
    if (!selectedEventId) return;
    setLoadingHourRequests(true);
    try {
      const { data: activities, error: err } = await supabase
        .from('volunteer_activities')
        .select('*')
        .eq('event_id', selectedEventId)
        .is('task_id', null)
        .order('created_at', { ascending: false });

      if (err || !activities) {
        setHourRequests([]);
        return;
      }

      const volunteerIds = activities.map((a: { volunteer_id: string }) => a.volunteer_id);
      const { profilesById } = await fetchProfilesByIds<HourRequestProfile>(
        volunteerIds,
        'id, first_name, last_name, profile_image',
        'useHourRequests',
      );

      const merged: HourRequest[] = activities.map((a) => {
        const row = a as HourRequest;
        return {
          ...row,
          task_title: null,
          profiles: profilesById[row.volunteer_id] || null,
        };
      });
      setHourRequests(merged);
    } catch {
      setHourRequests([]);
    } finally {
      setLoadingHourRequests(false);
    }
  }, [selectedEventId]);

  const handleAcceptHourRequest = useCallback(
    async (request: HourRequest, overrideMinutes?: number) => {
      if (!selectedEventId || !userId) return;
      const minutes = overrideMinutes ?? hoursToMinutes(request.hours);
      const durationError = validateDuration(minutes);
      if (durationError) {
        onError?.(durationError);
        return;
      }
      try {
        const { data: currentVol, error: fetchErr } = await supabase
          .from('volunteers')
          .select('hours')
          .eq('volunteer_id', request.volunteer_id)
          .eq('event_id', selectedEventId)
          .maybeSingle();

        if (fetchErr) {
          onError?.(fetchErr.message || 'Failed to load volunteer');
          return;
        }
        if (currentVol == null) {
          onError?.('Volunteer not found for this event');
          return;
        }

        const newHours = addToTotal(currentVol.hours, minutes);
        const { error: updateErr } = await supabase
          .from('volunteers')
          .update({ hours: newHours })
          .eq('volunteer_id', request.volunteer_id)
          .eq('event_id', selectedEventId);

        if (updateErr) {
          onError?.(updateErr.message || 'Could not add hours');
          return;
        }

        const { error: insertErr } = await supabase.from('accepted_volunteer_hours').insert({
          volunteer_id: request.volunteer_id,
          event_id: request.event_id,
          task_id: request.task_id ?? null,
          start_time: request.start_time,
          end_time: request.end_time,
          hours: minutesToHours(minutes),
          description: request.description,
          original_activity_id: request.id,
          accepted_by: userId,
        });

        if (insertErr) {
          onError?.(insertErr.message || 'Failed to record hours');
          return;
        }

        // Clear FK before deleting volunteer_activities (matches app/pages/volunteers.tsx)
        const { error: clearFkErr } = await supabase
          .from('accepted_volunteer_hours')
          .update({ original_activity_id: null })
          .eq('original_activity_id', request.id);

        if (clearFkErr) {
          onError?.(clearFkErr.message || 'Failed to finalize accepted hours');
          return;
        }

        const { error: deleteErr } = await supabase.from('volunteer_activities').delete().eq('id', request.id);
        if (deleteErr) {
          onError?.(deleteErr.message || 'Failed to clear request');
          return;
        }

        setHourRequests((prev) => prev.filter((r) => r.id !== request.id));
        setHourRequestsCount((prev) => Math.max(0, prev - 1));
        refetch();
        onSuccess?.();
      } catch (err) {
        onError?.((err instanceof Error && err.message) || 'Failed to accept request');
        logger.error('Error accepting hour request:', err);
      }
    },
    [selectedEventId, userId, refetch, onError, onSuccess],
  );

  const handleDenyHourRequest = useCallback(
    async (requestId: string) => {
      try {
        const { error } = await supabase.from('volunteer_activities').delete().eq('id', requestId);
        if (error) {
          callbacks?.onError?.(error.message || 'Failed to deny request');
          return;
        }
        setHourRequests((prev) => prev.filter((r) => r.id !== requestId));
        setHourRequestsCount((prev) => Math.max(0, prev - 1));
      } catch (err) {
        callbacks?.onError?.((err instanceof Error && err.message) || 'Failed to deny request');
        logger.error('Error denying hour request:', err);
      }
    },
    [callbacks],
  );

  useEffect(() => {
    if (selectedEventId) fetchHourRequestsCount();
  }, [selectedEventId, fetchHourRequestsCount]);

  return {
    hourRequests,
    hourRequestsCount,
    loadingHourRequests,
    showHourRequestsModal,
    setShowHourRequestsModal,
    fetchHourRequests,
    fetchHourRequestsCount,
    handleAcceptHourRequest,
    handleDenyHourRequest,
  };
}
